import { Checkbox, Css, Icon, Palette, TextField, useTestIds } from "@homebound/beam";
import { observer } from "mobx-react";
import { ReactNode, useCallback, useState } from "react";
import { useInsideMapsConfig } from "../hooks/useInsideMapsConfig";
import { MaterialVariantOption, Texture } from "../types";

interface TexturePanelProps {
  materialVariants: MaterialVariantOption[];
  onSelectTexture: (index: number) => void;
  locationSelector?: ReactNode;
}

export const TexturePanel = observer(function TexturePanel({
  materialVariants,
  onSelectTexture,
  locationSelector,
}: TexturePanelProps) {
  const store = useInsideMapsConfig();
  const tid = useTestIds({});
  const [searchFilter, setSearchFilter] = useState("");
  const part = store.selectedPart;
  const cameraId = store.selectedCameraId;

  if (!part || !cameraId) {
    return (
      <div css={Css.tac.gray500.mt4.$} {...tid.texturePanel}>
        Select a part from the sidebar to view textures.
      </div>
    );
  }

  const filteredTextures = searchFilter
    ? part.textures.filter((t) => t.name.toLowerCase().includes(searchFilter.toLowerCase()))
    : part.textures;

  return (
    <div {...tid.texturePanel}>
      {/* Header */}
      <div css={Css.df.aic.jcsb.mb2.$}>
        <div css={Css.lg.add("fontWeight", "600").$}>Textures for: {part.name}</div>
        <button
          css={
            Css.pyPx(6)
              .pxPx(14)
              .br4.sm.cursorPointer.red600.add("background", "none")
              .add("border", "1px solid #dc2626").$
          }
          onClick={() => store.clearAllMaterials(cameraId, part.id)}
        >
          Clear All Materials
        </button>
      </div>

      {locationSelector}

      {/* Search textures */}
      <TextField
        label="Search textures..."
        labelStyle="hidden"
        value={searchFilter}
        onChange={(val) => setSearchFilter(val ?? "")}
        placeholder="Search textures..."
      />

      {/* Texture rows */}
      <div css={Css.mt2.$}>
        {filteredTextures.map((texture) => {
          const realIndex = part.textures.indexOf(texture);
          return (
            <TextureRow
              key={`${texture.name}-${realIndex}`}
              texture={texture}
              textureIndex={realIndex}
              cameraId={cameraId}
              partId={part.id}
              isSelected={store.selectedTextureIndex === realIndex}
              onSelect={() => onSelectTexture(realIndex)}
              materialVariants={materialVariants}
            />
          );
        })}
      </div>
    </div>
  );
});

const TextureRow = observer(function TextureRow({
  texture,
  textureIndex,
  cameraId,
  partId,
  isSelected,
  onSelect,
  materialVariants,
}: {
  texture: Texture;
  textureIndex: number;
  cameraId: string;
  partId: string;
  isSelected: boolean;
  onSelect: () => void;
  materialVariants: MaterialVariantOption[];
}) {
  const store = useInsideMapsConfig();
  const tid = useTestIds({});
  const mvCount = texture.materialVariantIds?.length ?? 0;

  const getMaterial = useCallback((mvId: string) => materialVariants.find((mv) => mv.id === mvId), [materialVariants]);

  return (
    <div
      css={
        Css.py2.px2.br8.mb1.cursorPointer.bb.bcGray100.if(isSelected).bgBlue50.add("borderLeft", "3px solid #2563eb").$
      }
      onClick={onSelect}
      {...tid.textureRow}
    >
      {/* Texture header row */}
      <div css={Css.df.aic.jcsb.$}>
        <div css={Css.df.aic.gap1.$}>
          <span css={Css.smSb.$}>{texture.name}</span>
          <span css={Css.xs.gray500.$}>({mvCount})</span>
        </div>
        <div css={Css.df.aic.gap3.$}>
          <Checkbox
            label="Verified"
            selected={texture.verified ?? false}
            onChange={(val) => store.verifyTexture(cameraId, partId, textureIndex, val)}
          />
          <Checkbox
            label="Skip"
            selected={texture.skipped ?? false}
            onChange={(val) => store.skipTexture(cameraId, partId, textureIndex, val)}
          />
        </div>
      </div>

      {/* Material cards */}
      {mvCount > 0 ? (
        <div css={Css.mt2.$}>
          {texture.materialVariantIds!.map((mvId) => {
            const mv = getMaterial(mvId);
            return (
              <div key={mvId} css={Css.df.aic.gap2.py1.px2.bgGray50.br8.mb1.$}>
                {/* Material thumbnail */}
                {mv?.imageUrl ? (
                  <img
                    src={mv.imageUrl}
                    alt={mv.displayName}
                    css={Css.wPx(48).hPx(48).br4.add("objectFit", "cover").add("flexShrink", "0").$}
                  />
                ) : (
                  <div css={Css.wPx(48).hPx(48).br4.bgGray200.df.aic.jcc.xs.gray400.add("flexShrink", "0").$}>img</div>
                )}
                {/* Material info */}
                <div css={Css.fg1.add("minWidth", "0").$}>
                  <div css={Css.sm.smSb.$}>{mv?.displayName ?? mvId}</div>
                  <div css={Css.xs.gray500.$}>
                    {mv?.costCodeNumber && (
                      <span>
                        Cost Code: {mv.costCodeNumber} {mv.costCodeDisplayName}
                      </span>
                    )}
                  </div>
                  <div css={Css.xs.gray500.$}>{mv?.brandName && <span>Brand: {mv.brandName}</span>}</div>
                  <div css={Css.xs.gray400.$}>ID: {mvId}</div>
                </div>
                {/* Checkmark */}
                <Icon icon="check" inc={2.5} color={Palette.Green600} xss={{ flexShrink: 0 }} />
              </div>
            );
          })}
        </div>
      ) : (
        <div css={Css.mt1.xs.red500.$}>No Materials Selected</div>
      )}
    </div>
  );
});
